/// <reference path="../../../global.d.ts" />
'use client';

import { useConstant } from '@cc-shopping/hooks/useConstant';
import { cloneElement, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { v4 as uuid } from 'uuid';
import { useAnalytics } from '../../hooks/useAnalytics';
import {
  DEFAULT_SEGMENT_SKU_VIEWED_LEVEL,
  DEFAULT_SEGMENT_SKU_VIEWED_PRODUCT_TYPE,
  DEFAULT_SEGMENT_SKU_VIEWED_SOURCE,
} from '../../lib/constants';
import { getCleanLocationHref } from '../../lib/urlUtils';
import { warnOnDevIfCloneElementWillFail } from '../../lib/utils';
import { EventName, type ShoppingFinishedPayload, type SkuViewedPayload } from '../../Shibuya';
import { useAnalyticsPageView } from '../PageView/Context';
import {
  type AnalyticsShoppingFinishedCallbackProps,
  AnalyticsSkuViewedContext,
  type AnalyticsSkuViewedContextValue,
} from './Context';

export interface AnalyticsSkuViewedProps {
  id: SkuViewedPayload['sku_id'];
  source?: SkuViewedPayload['sku_source'];
  name: SkuViewedPayload['sku_name'];
  productType?: SkuViewedPayload['sku_product_type'];
  level?: SkuViewedPayload['sku_level'];
  institutionId?: SkuViewedPayload['institution_id'];
  institutionName?: SkuViewedPayload['institution_name'];
  productId?: SkuViewedPayload['product_id'];
  productName?: SkuViewedPayload['product_name'];
  offerId?: SkuViewedPayload['offer_id'];
  offerName?: SkuViewedPayload['offer_name'];
  monetizable: SkuViewedPayload['monetizable'];
  impressionSection: SkuViewedPayload['impression_section'];
  impressionPosition: SkuViewedPayload['impression_position'];
  additionalAttributes?: SkuViewedPayload['additional_attributes'];
  dangerouslySetShoppingFinishedId?: string;
  overrideSkuImpressionId?: string;
  direction?: 'horizontal' | 'vertical';
  scrollableAncestor?: HTMLElement;
  enabled?: boolean;
  children?: React.JSX.Element | ((context: AnalyticsSkuViewedContextValue) => React.JSX.Element);
}

export const AnalyticsSkuViewed = forwardRef<HTMLElement, AnalyticsSkuViewedProps>(
  (
    {
      children,
      dangerouslySetShoppingFinishedId,
      scrollableAncestor,
      overrideSkuImpressionId,
      enabled = true,
      ...props
    },
    ref,
  ) => {
    const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
    const { pageViewId, pageName, pageViewReady } = useAnalyticsPageView();
    const [skuViewedPayload, setSkuViewedPayload] = useState<SkuViewedPayload>();
    const skuImpressionId = useConstant(() => uuid());
    const shoppingFinishedId = useConstant(() => {
      return dangerouslySetShoppingFinishedId || uuid().replace(/-/g, '');
    }, ['dangerouslySetShoppingFinishedId']);

    const additionalAttributesRef = useRef(props.additionalAttributes);
    additionalAttributesRef.current = props.additionalAttributes;

    const { ref: inViewRef, inView } = useInView({
      root: scrollableAncestor ? scrollableAncestor || 'window' : null,
      skip: !enabled,
      rootMargin: scrollableAncestor ? '0px' : '1px',
      threshold: 0,
      triggerOnce: true,
    });

    const setRefs = useCallback(
      (node: HTMLElement | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && 'current' in ref) {
          ref.current = node;
        }
        inViewRef(node);
      },
      [inViewRef],
    );

    useEffect(() => {
      if (pageViewReady && deviceId) {
        const eventPayload: SkuViewedPayload = {
          sku_id: props.id,
          sku_source: props.source ?? DEFAULT_SEGMENT_SKU_VIEWED_SOURCE,
          sku_name: props.name,
          sku_product_type: props.productType ?? DEFAULT_SEGMENT_SKU_VIEWED_PRODUCT_TYPE,
          sku_level: props.level ?? DEFAULT_SEGMENT_SKU_VIEWED_LEVEL,
          institution_id: props.institutionId,
          institution_name: props.institutionName,
          product_id: props.productId,
          product_name: props.productName,
          offer_id: props.offerId,
          offer_name: props.offerName,
          monetizable: props.monetizable,
          impression_page_name: pageName || window.location.pathname,
          impression_page_url: getCleanLocationHref(),
          impression_pageview_id: pageViewId,
          impression_section: props.impressionSection,
          impression_position: props.impressionPosition,
          sku_impression_id: overrideSkuImpressionId ?? skuImpressionId,
          deployable_name: deployableName,
          deployable_version: deployableVersion,
          traditional_device_id: deviceId,
          ...(props.additionalAttributes ?? {}),
        };

        setSkuViewedPayload(eventPayload);
      }
    }, [pageViewReady, deviceId]);

    useEffect(() => {
      if (inView && skuViewedPayload) {
        const eventPayload = { ...skuViewedPayload, ...(additionalAttributesRef.current ?? {}) };
        analytics?.track(EventName.SKU_VIEWED, eventPayload, {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        });

        window.dataLayer?.push({
          event: 'SkuViewed',
          ...eventPayload,
        });
      }
    }, [inView, skuViewedPayload]);

    const fireShoppingFinishedEvent = useCallback(
      ({
        destinationUrl,
        ctaType,
        finishType = 'external_application',
        monetizable = skuViewedPayload?.monetizable,
        interstitialClientBotSuspected,
        revenueAmount,
        revenueType,
        entryPointPageUrl,
        estimatedRevenue,
        overrideShoppingFinishedId,
      }: AnalyticsShoppingFinishedCallbackProps) => {
        if (skuViewedPayload) {
          const eventPayload: ShoppingFinishedPayload = {
            ...skuViewedPayload,
            ...(additionalAttributesRef.current ?? {}),
            monetizable: monetizable,
            destination_url: destinationUrl,
            cta_type: ctaType,
            finish_type: finishType,
            interstitial_client_bot_suspected: interstitialClientBotSuspected,
            revenue_amount: revenueAmount,
            revenue_type: revenueType,
            shopping_finish_id: overrideShoppingFinishedId ?? shoppingFinishedId,
            entry_point_page_url: entryPointPageUrl,
            ...(estimatedRevenue && { estimated_revenue: estimatedRevenue }),
          };

          analytics?.track(EventName.SHOPPING_FINISHED, eventPayload, {
            context: {
              app: {
                name: deployableName,
                version: gitSha,
              },
            },
          });
          window.dataLayer?.push({
            event: 'ShoppingFinished',
            ...eventPayload,
          });
        }
      },
      [skuViewedPayload],
    );

    const context = useMemo<AnalyticsSkuViewedContextValue>(
      () => ({
        skuImpressionId: overrideSkuImpressionId ?? skuImpressionId,
        shoppingFinishedId,
        skuViewedPayload,
        fireShoppingFinishedEvent,
      }),
      [
        skuImpressionId,
        overrideSkuImpressionId,
        shoppingFinishedId,
        skuViewedPayload,
        fireShoppingFinishedEvent,
      ],
    );

    const child = typeof children === 'function' ? children(context) : children;
    if (!child) {
      throw new Error('Missing Child');
    }

    warnOnDevIfCloneElementWillFail(child, 'AnalyticsSkuViewed');
    return (
      <AnalyticsSkuViewedContext.Provider value={context}>
        {cloneElement(child, {
          ref: setRefs,
        })}
      </AnalyticsSkuViewedContext.Provider>
    );
  },
);
